import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Download, Palette, Type, ImageIcon, FileText, Copy, Check, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const COLORS = [
  { name: "Signal Yellow", hex: "#FCDF4A", role: "Primary — CTAs, highlights. Text on it is always black." },
  { name: "Blueprint Navy", hex: "#050A14", role: "Background — every page surface." },
  { name: "Panel Navy", hex: "#0C1322", role: "Cards & raised panels." },
  { name: "Steel Navy", hex: "#182239", role: "Secondary buttons & fills." },
  { name: "Draft Blue", hex: "#3C83F6", role: "Links, charts, info accents." },
  { name: "Paper White", hex: "#F8FAFC", role: "Primary text on dark." },
  { name: "Slate", hex: "#8B9BB1", role: "Muted text & captions." },
  { name: "Gridline", hex: "#263040", role: "Borders, dividers, blueprint grid." },
  { name: "Approve Green", hex: "#2EB88A", role: "Success & savings deltas." },
  { name: "Amber", hex: "#F5A623", role: "Email & PDF accent." },
  { name: "Alert Red", hex: "#DC2828", role: "Errors & over-budget." },
];

const FONTS = [
  { font: "Chivo", weights: "700–900", role: "Headlines & display", cls: "font-heading text-3xl font-extrabold tracking-tighter", sample: "Build your blueprint." },
  { font: "IBM Plex Sans", weights: "400–700", role: "Body & UI copy", cls: "text-lg", sample: "Costobuild turns ideas into blueprint-ready scopes of work." },
  { font: "IBM Plex Mono", weights: "400–600", role: "Labels, prices & data", cls: "font-mono2 text-sm uppercase tracking-[0.25em]", sample: "From Plans to Possibility" },
];

const LOGOS = [
  { file: "/logo.png", label: "Primary mark", note: "512px · transparent PNG", dl: "costobuild-logo-512.png" },
  { file: "/logo192.png", label: "Small mark", note: "192px · transparent PNG", dl: "costobuild-logo-192.png" },
  { file: "/og-card.png", label: "Social share card", note: "1200×630 · Open Graph", dl: "costobuild-og-card.png", wide: true },
];

const RULES = [
  ["Do", "Use the logo on Blueprint Navy (#050A14) or white backgrounds only."],
  ["Do", "Keep clear space around the mark equal to 25% of its width."],
  ["Do", "Pair the mark with COSTOBUILD set in Chivo Black, uppercase."],
  ["Don't", "Recolor, rotate, outline, add shadows or place the mark on busy photos."],
  ["Don't", "Stretch the mark or render it smaller than 24px."],
];

export default function Brand() {
  const [copied, setCopied] = useState("");
  const copyHex = (hex) => {
    navigator.clipboard?.writeText(hex);
    setCopied(hex);
    toast.success(`${hex} copied`);
    setTimeout(() => setCopied(""), 1500);
  };

  const lbl = "font-mono2 text-xs uppercase tracking-[0.2em] text-primary";

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-8">
        <p className={lbl}><Palette className="mr-2 inline h-3.5 w-3.5" />Brand Kit</p>
        <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl">Costobuild design package</h1>
        <p className="mt-3 max-w-xl text-muted-foreground">
          Everything partners and publishers need to represent Costobuild correctly — logos, colors,
          typography and usage rules. Share this page or download the full kit.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="/brand/costobuild-brand-kit.zip" download data-testid="download-brand-kit">
            <Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              <Download className="mr-2 h-4 w-4" /> Download Full Kit (.zip)
            </Button>
          </a>
          <a href="/brand/Costobuild-Brand-Guidelines.pdf" target="_blank" rel="noreferrer" data-testid="download-brand-pdf">
            <Button variant="outline" className="rounded-sm border-border font-mono2 uppercase tracking-widest">
              <FileText className="mr-2 h-4 w-4" /> Guidelines PDF
            </Button>
          </a>
        </div>

        {/* Logos */}
        <section className="mt-14">
          <p className={lbl}><ImageIcon className="mr-2 inline h-3.5 w-3.5" />01 · Logos</p>
          <div className="mt-4 grid gap-px border border-border bg-border sm:grid-cols-3" data-testid="brand-logos">
            {LOGOS.map((l) => (
              <div key={l.file} className={`flex flex-col bg-background p-6 ${l.wide ? "sm:col-span-1" : ""}`}>
                <div className="flex h-40 items-center justify-center border border-border bg-secondary/10">
                  <img src={l.file} alt={l.label} className={l.wide ? "max-h-32 w-auto" : "h-24 w-24"} />
                </div>
                <div className="mt-4 flex items-end justify-between gap-2">
                  <div>
                    <div className="font-heading text-sm font-semibold">{l.label}</div>
                    <div className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{l.note}</div>
                  </div>
                  <a href={l.file} download={l.dl} className="shrink-0">
                    <Button size="sm" variant="outline" className="h-7 rounded-sm border-border font-mono2 text-[10px] uppercase tracking-widest">
                      <Download className="mr-1 h-3 w-3" /> PNG
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Colors */}
        <section className="mt-14">
          <p className={lbl}><Palette className="mr-2 inline h-3.5 w-3.5" />02 · Colors</p>
          <p className="mt-2 text-sm text-muted-foreground">Click any swatch to copy its hex code.</p>
          <div className="mt-4 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-3" data-testid="brand-colors">
            {COLORS.map((c) => (
              <button key={c.hex} onClick={() => copyHex(c.hex)} data-testid={`color-${c.hex.slice(1)}`}
                className="group flex items-center gap-4 bg-background p-4 text-left transition-colors hover:bg-secondary/20">
                <span className="h-12 w-12 shrink-0 border border-border" style={{ backgroundColor: c.hex }} />
                <span className="min-w-0">
                  <span className="block font-heading text-sm font-semibold">{c.name}</span>
                  <span className="flex items-center gap-1.5 font-mono2 text-xs text-primary">
                    {c.hex} {copied === c.hex ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">{c.role}</span>
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section className="mt-14">
          <p className={lbl}><Type className="mr-2 inline h-3.5 w-3.5" />03 · Typography</p>
          <div className="mt-4 divide-y divide-border border border-border" data-testid="brand-typography">
            {FONTS.map((f) => (
              <div key={f.font} className="grid gap-3 p-6 sm:grid-cols-3">
                <div>
                  <div className="font-heading text-base font-bold">{f.font}</div>
                  <div className="font-mono2 text-[10px] uppercase tracking-widest text-primary">{f.role}</div>
                  <div className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">Weights {f.weights} · Google Fonts</div>
                </div>
                <div className={`sm:col-span-2 ${f.cls}`}>{f.sample}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Usage rules */}
        <section className="mt-14">
          <p className={lbl}><ShieldCheck className="mr-2 inline h-3.5 w-3.5" />04 · Usage rules</p>
          <div className="mt-4 border border-border" data-testid="brand-rules">
            {RULES.map(([kind, rule], i) => (
              <div key={i} className="flex items-start gap-3 border-b border-border px-5 py-3 last:border-b-0">
                <span className={`mt-0.5 shrink-0 border px-1.5 py-0.5 font-mono2 text-[9px] uppercase tracking-widest ${
                  kind === "Do" ? "border-green-500/50 text-green-400" : "border-red-500/50 text-red-400"}`}>{kind}</span>
                <span className="text-sm text-muted-foreground">{rule}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">
            Tagline lockup: <span className="text-primary">"From Plans to Possibility"</span> · IBM Plex Mono, letterspaced
          </p>
        </section>
      </div>
      <Footer />
    </div>
  );
}
